/**
 * Quoted history of a reply/forward, put into the editor when the composer opens (DESIGN.md T11,
 * like Outlook/OWA): an empty paragraph for the cursor, then the quote block (`data-gx-quote`,
 * editor node `mailQuote`) with the "From / Sent / To / Cc / Subject" header in the user's
 * language and the original below. The signature goes between the two (editor `setSignature`).
 *
 * The original HTML is sanitized (DOMPurify) and reduced by the editor schema. Pictures:
 * - `cid:` inline pictures point to the platform API (`inlineImageUrl`); on send they become
 *   `cid:` again (`quotedImageContentId`), so the server attaches the original's parts;
 * - remote pictures stay only when the user allowed remote images for that message;
 * - embedded data: pictures stay (they are sent as new inline images).
 * Our signature marker inside the original is dropped, so the editor sees one signature only.
 */
import DOMPurify from 'dompurify';
import { plainTextToEditorHtml } from '../editor/plain-text';
import { escapeHtml } from './sanitize';

export interface QuoteInput {
  /** Header rows in display order, already localized and formatted: [label, value]. */
  header: readonly (readonly [string, string])[];
  /** Original body: HTML, or plain text (quoted line by line). */
  body: { format: 'html' | 'text'; content: string };
  /** Remote images were allowed for the original (reading pane "Show images"). */
  allowRemote: boolean;
  /** URL that shows the original's inline picture with this Content-ID (undefined: dropped). */
  inlineImageUrl: (contentId: string) => string | undefined;
}

const QUOTE_ATTRIBUTE = 'data-gx-quote';
const SIGNATURE_ATTRIBUTE = 'data-gx-sig';

/** `cid:abc@x` → `abc@x` (URL-decoded), else undefined. */
export function contentIdOf(src: string): string | undefined {
  const match = /^\s*cid:(.+)$/i.exec(src);
  if (!match?.[1]) return undefined;
  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
}

function rewriteImages(root: HTMLElement, input: QuoteInput): void {
  for (const image of Array.from(root.querySelectorAll('img'))) {
    const src = image.getAttribute('src') ?? '';
    const cid = contentIdOf(src);
    const url = cid === undefined ? undefined : input.inlineImageUrl(cid);
    if (url !== undefined) image.setAttribute('src', url);
    else if (/^data:image\//i.test(src)) continue;
    else if (input.allowRemote && /^https:\/\//i.test(src)) continue;
    else image.remove();
  }
}

/** Sanitized original as editor HTML. */
function originalHtml(input: QuoteInput): string {
  if (input.body.format === 'text') return plainTextToEditorHtml(input.body.content);
  const sanitized = DOMPurify.sanitize(input.body.content, { FORBID_TAGS: ['style', 'form', 'input', 'button'], FORBID_ATTR: ['id', 'class'] });
  const clean = new DOMParser().parseFromString(sanitized, 'text/html').body;
  rewriteImages(clean, input);
  for (const marked of Array.from(clean.querySelectorAll(`[${SIGNATURE_ATTRIBUTE}]`))) marked.removeAttribute(SIGNATURE_ATTRIBUTE);
  // An earlier quote inside the original stays history (nested quote block).
  const html = clean.innerHTML.trim();
  return html === '' ? '<p></p>' : html;
}

/** Editor content of a new reply/forward: cursor paragraph + quote block (see module comment). */
export function buildQuotedBody(input: QuoteInput): string {
  const header = input.header.map(([label, value]) => `<strong>${escapeHtml(label)}</strong> ${escapeHtml(value)}`).join('<br>');
  return `<p></p><div ${QUOTE_ATTRIBUTE}=""><p>${header}</p><p></p>${originalHtml(input)}</div>`;
}
